/**
 * Hi! Welcome to the Phaneroo Prayer Requests categories store.
 *
 * - All API fetch requests are actually handled inside the `categories.service.js` file.
 * - Every action has a corresponding $axios request that performs validation and request handling.
 * - Tests for this store module are located in './tests/categories.test.js'
 *
 * @todo
 * - Perform data transforms for dashboard UI
 * - Write tests for replies store.
 * - Require collect token in $axios instance from session storage
 *   instead of passing it as param.
 */

import '../../typedefs'
import { getCategories, getCategory, createCategory, updateCategory, deleteCategory } from '../../services/categories.service'
import { assign, isObject } from 'lodash-es'
import { indexArrayBy } from '../../utils'

const __DEV__ = process.env.NODE_ENV !== 'production'

export default {
  namespaced: true,
  state: {
    /**
     * @type {Category[]}
     */
    categories: [],
    /**
     * @type {Category}
     */
    category: undefined
  },
  getters: {
    /**
     * @returns {Category[]}
     */
    allCategories: state => state.categories,
    /**
     * @returns {Object<Category>}
     */
    categories: state => state.categories
      ? indexArrayBy(state.categories, 'id')
      : {},

    /**
     * Gets a category by id
     * @returns {Object<Category>}
     */
    categoryById: (_state, getters) => (id) => {
      const { categories } = getters
      if (!isObject(categories)) return undefined
      return categories[id]
    }
  },
  mutations: {
    /**
     * Sets array of categories to active state
     * @param {Object} state state
     * @param {Category[]} payload Category array
     */
    SET_CATEGORIES (state, payload) {
      state.categories = payload
    },
    /**
     * Sets specific category to active state
     * @param {Object} state state
     * @param {Category} payload New category object
     */
    SET_CATEGORY (state, payload) {
      state.category = payload
    },
    /**
     * Adds new category to state
     * @param {Object} state state
     * @param {Category} payload Category
     */
    ADD_CATEGORY (state, payload) {
      state.categories.push(payload)
    },
    /**
     * Updates specific category's object state
     * @param {Object} state state
     * @param {Category} payload Category
     */
    UPDATE_CATEGORY (state, payload) {
      const targetIndex = state.categories.findIndex(category => category.id === payload.id)
      assign(state.categories[targetIndex], payload)
    },
    /**
     * Deletes specific category from state
     * @param {Object} state state
     * @param {String} id Target category id
     */
    DELETE_CATEGORY (state, id) {
      const targetIndex = state.categories.findIndex(category => category.id === id)
      state.categories.splice(targetIndex, 1)
    }
  },
  actions: {
    /**
     * Gets all prayer request categories
     * @param {VuexContext} context Store context
     */
    async getCategories ({ commit, rootState }) {
      const authToken = rootState.auth.authToken
      const response = await getCategories(authToken)
      const categories = response.data
      commit('SET_CATEGORIES', categories)
    },
    /**
     * Gets single category by id
     * @param {VuexContext} context Store context
     * @param {String} id Category id
     */
    async getCategory ({ commit, rootState }, id) {
      const authToken = rootState.auth.authToken
      const response = await getCategory(id, authToken)
      const category = response.data
      commit('SET_CATEGORY', category)
    },
    /**
     * Creates a new prayer request category
     * @param {VuexCategory} context Store context
     * @param {{ name: String, description: String }} payload Category data
     */
    async createCategory ({ commit, rootState }, payload) {
      const authToken = rootState.auth.authToken
      const response = await createCategory(authToken, payload)
      const newCategory = response.data.category
      commit('ADD_CATEGORY', newCategory)
    },
    /**
     * Updates a prayer requests category
     * @param {VuexCategory} context Store context
     * @param {{ id: String, description: String }} payload Category data
     */
    async updateCategory ({ commit, rootState }, { id, payload }) {
      const authToken = rootState.auth.authToken
      const response = updateCategory(authToken, id, payload)
      const category = response.data.category
      commit('UPDATE_CATEGORY', category)
    },
    /**
     * Deletes a prayer requests category
     * @param {VuexCategory} context Store context
     * @param {String} id Category id
     */
    async deleteCategory ({ commit, rootState }, id) {
      const authToken = rootState.auth.authToken
      const response = await deleteCategory(authToken, id)
      const { message } = response
      __DEV__ && console.log(`Deleted Category of id: ${id}:`, message)
      commit('DELETE_CATEGORY', id)
    }
  }
}
